export default class Result<T, E> {
  static ok<T>(value: T) {
    return new Result<T, never>("ok", value);
  }

  static err<E>(error: E) {
    return new Result<never, E>("err", error);
  }

  private readonly value: T = undefined!;
  private readonly error: E = undefined!;

  private constructor(
    public readonly status: "ok" | "err",
    result: T | E
  ) {
    if (status === "ok") {
      this.value = result as T;
    } else {
      this.error = result as E;
    }
  }

  isOk() {
    return this.status === "ok";
  }

  isErr() {
    return this.status === "err";
  }

  unwrap() {
    if (this.isErr()) throw this.error;
    return this.value;
  }

  unwrapOr(defaultValue: T) {
    if (this.isErr()) return defaultValue;
    return this.value;
  }

  map<U, E2 = never>(fn: (value: T) => U | Result<U, E2>): Result<U, E | E2> {
    if (this.isErr()) return Result.err(this.error);
    const res = fn(this.value);
    if (res instanceof Result) return res;
    return Result.ok(res);
  }

  mapErr<EIn extends E, EOut, T2 = never>(
    constructor: new (...args: any[]) => EIn,
    fn: (error: EIn) => EOut | Result<T2, EOut>
  ): Result<T | T2, EOut | Exclude<E, EIn>>;
  mapErr<U, T2 = never>(fn: (error: E) => U | Result<T2, U>): Result<T | T2, U>;
  mapErr(
    constructorOrFn: (new (...args: any[]) => E) | ((error: E) => any),
    fn?: (error: E) => any
  ): Result<T, E> {
    if (this.isOk()) return Result.ok(this.value);
    if (fn && !(this.error instanceof constructorOrFn)) return this;

    fn ??= constructorOrFn as (error: E) => any;
    const res = fn(this.error);

    if (res instanceof Result) return res;
    return Result.err(res);
  }

  match<U>(ok: (value: T) => U, err: (error: E) => U) {
    if (this.isErr()) return err(this.error);
    return ok(this.value);
  }
}
